// Diagnostics client — POSTs events to the central diagnostics API.
// Backend endpoint is provided by the Bitol services (cross-app event stream).
// On failure we log to console and continue; we never let diagnostics break the UI.
//
// Also keeps a small in-memory ring buffer of the most recent client-side
// errors so the self-issue-report panel (#65) can attach them as session
// context. The buffer is process-local — no persistence, no PII leaving the
// tab unless the user explicitly submits a report.

const ENDPOINT = (import.meta.env.VITE_DIAGNOSTICS_URL || '').trim()
const SOURCE_APP = 'next.actianlabs.com'

const RECENT_BUFFER_SIZE = 20
const recentErrors = []

function pushRecent(entry) {
  recentErrors.push(entry)
  if (recentErrors.length > RECENT_BUFFER_SIZE) {
    recentErrors.splice(0, recentErrors.length - RECENT_BUFFER_SIZE)
  }
}

export function reportDiagnostic({ severity = 'error', source, title, message, details } = {}) {
  pushRecent({
    severity,
    source: source || 'webapp',
    title,
    message,
    details: details ? String(details) : undefined,
    occurredAt: new Date().toISOString(),
  })

  if (!ENDPOINT) return

  const payload = {
    app: SOURCE_APP,
    severity,
    source: source || 'webapp',
    title,
    message,
    details: details ? String(details) : undefined,
    occurredAt: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  }

  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((err) => console.warn('[diagnostics] post failed', err))
  } catch (err) {
    console.warn('[diagnostics] threw', err)
  }
}

// Returns up to `limit` most-recent recorded events, newest first. Used by
// the self-report panel to attach a session context block.
export function getRecentErrors(limit = 10) {
  const n = Math.max(0, Math.min(limit, recentErrors.length))
  return recentErrors.slice(-n).reverse().map((e) => ({ ...e }))
}

export function installGlobalErrorReporter() {
  if (typeof window === 'undefined') return

  window.addEventListener('error', (event) => {
    reportDiagnostic({
      severity: 'error',
      source: 'window.onerror',
      title: 'Uncaught error',
      message: event.message,
      details: event.error?.stack,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportDiagnostic({
      severity: 'error',
      source: 'unhandledrejection',
      title: 'Unhandled promise rejection',
      message: String(event.reason?.message || event.reason),
      details: event.reason?.stack,
    })
  })
}
